import React from 'react'
import { differentialFlamegraphBaseValue, DifferentialFlamegraphNode, differentialFlamegraphPercentageChange } from '../../domain/Trace'
import { BoxBody, BoxHeader, BoxTitle } from '../../ui/box'
import Milliseconds from '../../helper/milliseconds'
import Tendency from '../../helper/tendency'
import Memory from '../../helper/memory'
import LargeNumber from '../../helper/large-number'
import { LayerReport, TraceDiffTranslation } from '../../domain/TraceSummary'
import { layersNameToClass, layersNameToLabel, layersLayerSupportsCount } from '../../domain/Layers'

interface Props {
    node: DifferentialFlamegraphNode
    layers?: { base: LayerReport; target: LayerReport }
    traceDiffTranslation: TraceDiffTranslation
}

export class DiffDetails extends React.PureComponent<Props> {
    public render() {
        const { node, layers, traceDiffTranslation } = this.props

        const layerNames = layers ? this.uniqueArray(Object.keys(layers.base), Object.keys(layers.target)) : null

        return (
            <>
                <BoxHeader>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
                        <BoxTitle className="tw-mt-0" muted>
                            <i className="fa fa-compress tw-mr-8 text-muted" />
                            Comparison from {traceDiffTranslation.from} <i className="fa fa-long-arrow-right" />
                            {traceDiffTranslation.to}
                        </BoxTitle>
                    </div>
                </BoxHeader>
                <BoxBody>
                    <div className="flamegraph-layerComparison tw-font-size-16 tw-mb-8">
                        <div className="half">
                            <small>Memory</small>
                            <br />
                            {node.values.mc > 0 || node.delta.mc != 0 ? (
                                <>
                                    <Memory value={differentialFlamegraphBaseValue(node.values.mc, node.delta.mc)} class="tw-mr-8" />
                                    <i className="fa fa-long-arrow-right tw-mr-8 text-muted" />
                                    <Memory value={node.values.mc} class="tw-mr-8" />
                                    <Tendency
                                        value={differentialFlamegraphPercentageChange(node.values.mc, node.delta.mc)}
                                        showValue={false}
                                        lessIsGood={true}
                                    />
                                </>
                            ) : (
                                '-'
                            )}
                        </div>
                        <div className="half">
                            <small>Time</small>
                            <br />
                            <Milliseconds value={differentialFlamegraphBaseValue(node.values.wt, node.delta.wt)} class="tw-mr-8" />
                            <i className="fa fa-long-arrow-right tw-mr-8 text-muted" />
                            <Milliseconds value={node.values.wt} class="tw-mr-8" />
                            <Tendency
                                value={differentialFlamegraphPercentageChange(node.values.wt, node.delta.wt)}
                                showValue={false}
                                lessIsGood={true}
                            />
                        </div>
                        {node.stats.ct != 1 || node.delta.ct != 0 ? (
                            <div className="half">
                                <small>Call Count</small>
                                <br />
                                <LargeNumber value={differentialFlamegraphBaseValue(node.stats.ct, node.delta.ct)} class="tw-mr-8" />
                                <i className="fa fa-long-arrow-right tw-mr-8 text-muted" />
                                <LargeNumber value={node.stats.ct} class="tw-mr-8" />
                                <Tendency
                                    value={differentialFlamegraphPercentageChange(node.stats.ct, node.delta.ct)}
                                    showValue={false}
                                    lessIsGood={true}
                                />
                            </div>
                        ) : null}
                        {layers && layerNames ? (
                            <>
                                <div className="divider" />

                                <div className="tw-font-weight-strong">Type</div>
                                <div className="tw-font-weight-strong">Calls</div>
                                <div className="tw-font-weight-strong half">Response Time</div>

                                {layerNames
                                    .sort((a, b) => (layers.base[b]?.wt ?? 0) - (layers.base[a]?.wt ?? 0))
                                    .map(layerName => (
                                        <React.Fragment key={layerName}>
                                            <div className="text-ellipsis">
                                                <small className="tw-mr-8">{layersNameToLabel(layerName)}</small>
                                            </div>
                                            <div className="text-ellipsis">
                                                {layersLayerSupportsCount(layerName) ? (
                                                    <>
                                                        <LargeNumber
                                                            value={layers.base[layerName]?.ct ?? 0}
                                                            class={layersNameToClass(layerName)}
                                                        />
                                                        <i className="fa fa-long-arrow-right tw-mr-4 tw-ml-4 text-muted" />
                                                        <LargeNumber
                                                            value={layers.target[layerName]?.ct ?? 0}
                                                            class={layersNameToClass(layerName)}
                                                        />
                                                    </>
                                                ) : null}
                                            </div>
                                            <div className="half">
                                                <Milliseconds value={layers.base[layerName]?.wt} class={layersNameToClass(layerName)} />
                                                <i className="fa fa-long-arrow-right tw-mr-8 tw-ml-8 text-muted" />
                                                <Milliseconds value={layers.target[layerName]?.wt} class={layersNameToClass(layerName)} />
                                                <Tendency
                                                    value={
                                                        (layers.target[layerName]?.wt - layers.base[layerName]?.wt) /
                                                        layers.base[layerName]?.wt
                                                    }
                                                    showValue={false}
                                                    lessIsGood={true}
                                                    className="tw-ml-8"
                                                />
                                            </div>
                                        </React.Fragment>
                                    ))}
                            </>
                        ) : null}
                    </div>
                </BoxBody>
            </>
        )
    }

    private uniqueArray(strings: string[], strings2: string[]): string[] {
        const unique: string[] = [...strings]

        strings2.forEach(value => {
            if (!unique.includes(value)) {
                unique.push(value)
            }
        })

        return unique
    }
}
